import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, ShieldCheck, HandHeart } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const sections = [
  { id: "home", key: "nav_home" },
  { id: "prayer", key: "nav_prayer" },
  { id: "announcements", key: "nav_announcements" },
  { id: "gallery", key: "nav_gallery" },
  { id: "about", key: "nav_about" },
  { id: "events", key: "nav_events" },
  { id: "madrasa", key: "nav_madrasa" },
  { id: "committee", key: "nav_committee" },
  { id: "contact", key: "nav_contact" },
];

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isHome = loc.pathname === "/";

  const handleNav = (id) => {
    setOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-900 grid place-items-center text-amber-400 font-serif-en text-xl shadow-sm">ن</div>
          <div className="leading-tight">
            <div className={`text-base md:text-lg font-semibold ${lang === "ta" ? "font-serif-ta" : "font-serif-en"} text-emerald-900`}>
              {lang === "ta" ? "மஸ்ஜித் அல் நூர்" : "Masjid Al Noor"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Kodungaiyur · Chennai</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {sections.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-${s.id}`}
              onClick={() => handleNav(s.id)}
              className="text-sm text-stone-700 hover:text-emerald-900 transition-colors"
            >
              {t(s.key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/causes"
            data-testid="donate-link"
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium bg-amber-500 text-emerald-950 rounded-full px-3 py-1.5 hover:bg-amber-400 transition-colors"
          >
            <HandHeart className="w-3.5 h-3.5" />
            {t("nav_donate")}
          </Link>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium border border-stone-300 hover:border-emerald-900 hover:text-emerald-900 rounded-full px-3 py-1.5 transition-colors"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === "en" ? "தமிழ்" : "English"}
          </button>
          <Link
            to="/admin"
            data-testid="admin-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-900 text-white rounded-full px-3 py-1.5 hover:bg-emerald-800 transition-colors"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {t("nav_admin")}
          </Link>
          <button data-testid="menu-toggle" className="lg:hidden p-2" onClick={() => setOpen((o) => !o)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-white">
          <div className="max-w-7xl mx-auto px-5 py-4 grid grid-cols-2 gap-3">
            {sections.map((s) => (
              <button
                key={s.id}
                data-testid={`mnav-${s.id}`}
                onClick={() => handleNav(s.id)}
                className="text-left text-sm text-stone-700 py-2"
              >
                {t(s.key)}
              </button>
            ))}
            <button data-testid="m-lang-toggle" onClick={toggle} className="text-left text-sm text-emerald-900 py-2">
              {lang === "en" ? "தமிழ்" : "English"}
            </button>
            <Link to="/admin" data-testid="m-admin-link" className="text-left text-sm text-emerald-900 py-2">
              {t("nav_admin")}
            </Link>
            <Link to="/causes" data-testid="m-donate-link" className="text-left text-sm text-amber-700 py-2 font-medium">
              {t("nav_donate")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
